import { httpClient } from '@texas/api/httpClient/httpClient';
import { EntityHistory, InquiryHistoryEvent } from '@texas/api/historyTypes';
import {
  AutoUpdateRequestWithData,
  AutoUpdateResponse,
} from '@texas/api/endpoints/autoUpdateTypes';

export interface TierPrice {
  id: number;
  quantity: number;
  price: number | null;
  isMain: boolean;
  basedOnId: number | null;
  basedOnPrice: number | null;
  acceptedDate: string | null;
  acceptedByUserId: number | null;
  acceptedByUserName: string | null;
  rejectedAt: string | null;
  returnedAt: string | null;
}

export interface TargetTier {
  id: number;
  quantity: number;
}

export interface SupplierInquiry {
  id: number;
  compositionGroupPurchaseInfoId: number;
  compositionGroupId: number;
  branchId: number;
  basedOnId: number | null;
  basedOnFormattedInquiryNumber: string | null;
  controlledById: number | null;
  created: string;
  archived: string | null;
  archivedByUserName: string | null;
  acceptedDate: string | null;
  acceptedByUserId: number | null;
  acceptedByUserName: string | null;
  acceptedPriceCount: number;
  priceCount: number;
  exchangeRate: number;
  currencyId: number;
  handlingFeeId: number | null;
  freightCostId: number | null;
  customsCodeId: number | null;
  sampleCharge: number;
  mouldCharge: number;
  surcharge: number;
  createdByUserId: number;
  inquiryNumber: string;
  formattedInquiryNumber: string;
  isMain: boolean;
  tierPrices: TierPrice[];
  tierPriceRounds: Record<number, number>;
  allTargetTiers: TargetTier[];
  mpqQuantity: number | null;
  supplierContactId: number | null;
  supplierContactName: string | null;
  supplierContactEmail: string | null;
  supplierId: number | null;
  supplierName: string;
  isMatrix: boolean;
  missingCellCount: number;
  decimalsInPrice: number;
  controlledInquiries: InquiryWithContext[];
  matrixNodeXName: string | null;
  matrixNodeYName: string | null;
  matrixNodeXValue: string;
  matrixNodeYValue: string;
  branchName: string;
  branchIdentifier: string;
  supplierNumber: string;
  version: number;
  basedOnGroupVersion: number | null;
  basedOnGroupName: string | null;
  controllerFormattedInquiryNumber: string | null;
  supplierPurchaseInfo: {
    exchangeRate: number;
    currencyId: number;
  } | null;
  sent: string | null;
  returned: string | null;
  internalOwnerId: number | null;
  internalOwnerName: string | null;
  articleId: number | null;
  articleItemCode: string | null;
  customerId: number;
  variantId: number;
  sampleLeadTime: number | null;
  bulkLeadTime: number | null;
  shipmentLeadTime: number | null;
  developmentCost: number | null;
  developmentRefundQuantity: number | null;
  developmentRefundTimeMonths: number | null;
  matchedOffers: MatchedOffer[];
  isChangedSinceLastTrack: boolean;
}

export interface InquiryByVariantFilters {
  variantId: number;
  supplierIds?: number[];
  optionIds?: number[];
  branchIds?: number[];
  cellX?: number;
  cellY?: number;
  showArchived?: boolean;
  inquiryNumber?: string;
  status?: InquiryStatusFilter;
}

export interface InquiryCountFilters {
  variantId: number;
  supplierIds?: number[];
  optionIds?: number[];
  branchIds?: number[];
  cellX?: number;
  cellY?: number;
  inquiryNumber?: string;
  isController: boolean;
}

export interface InquiryCounts {
  open: number;
  sent: number;
  pricesReceived: number;
  accepted: number;
  archived: number;
}

export type InquiryStatusFilter =
  | 'open'
  | 'accepted'
  | 'sent'
  | 'prices-received';

export interface InquiryWithContext {
  supplierName: string;
  branchIdentifier: string;
  compositionGroupPurchaseInfoId: number;
  compositionGroupId: number;
  defaultCurrencyId: number;
  matrixNodeXValue: string;
  matrixNodeYValue: string;
  version: number;
  basedOnGroupVersion: number | null;
  basedOnGroupName: string | null;
  mainProductApprovalNumber: string | null;
  isController: boolean;
  controlledById: number | null;
  id: number;
  variantId: number;
  inquiryNumber: string;
  formattedInquiryNumber: string;
  controllerFormattedInquiryNumber: string | null;
  isMain: boolean;
  currencyId: number;
  exchangeRate: number;
  sampleCharge: number;
  mouldCharge: number;
  surcharge: number;
  sampleLeadTime: number | null;
  bulkLeadTime: number | null;
  shipmentLeadTime: number | null;
  developmentCost: number | null;
  developmentRefundQuantity: number | null;
  developmentRefundTimeMonths: number | null;
  mpqQuantity: number | null;
  supplierContactId: number | null;
  supplierContactName: string | null;
  supplierContactEmail: string | null;
  supplierId: number | null;
  sent: string | null;
  internalOwnerId: number | null;
  internalOwnerName: string | null;
  handlingFeeId: number | null;
  freightCostId: number | null;
  customsCodeId: number | null;
  created: string;
  archived: string | null;
  acceptedDate: string | null;
  acceptedByUserId: number | null;
  acceptedByUserName: string | null;
  acceptedPriceCount: number;
  priceCount: number;
  basedOnId: number | null;
  inquiryControlledById: number | null;
  tierPrices: TierPrice[];
  allTargetTiers: TargetTier[];
  missingCellCount: number;
  coveredCellCount: number;
  totalCellCount: number;
  minimumPrice: number | null;
  maximumPrice: number | null;
  latestResponse: string | null;
  matchedOffers: MatchedOffer[];
}

export interface MatchedOffer {
  offerGroupId: number;
  offerNumber: string | null;
}

export interface InquiryVersionSlot {
  supplierName: string;
  branchIdentifier: string;
  compositionGroupPurchaseInfoId: number;
  compositionGroupId: number;
  defaultCurrencyId: number;
  matrixNodeXValue: string;
  matrixNodeYValue: string;
  version: number;
  basedOnGroupVersion: number | null;
  basedOnGroupName: string | null;
  mainProductApprovalNumber: string | null;
  isController: boolean;
  controlledById: number | null;
  supplierId: number | null;
  canCreateInquiry: boolean;
}

export interface InquiryPageData {
  versions: InquiryVersionSlot[];
  inquiries: InquiryWithContext[];
}

export interface UpdateInquiryRequest {
  currencyId: number;
  exchangeRate: number;
  handlingFeeId?: number | null;
  freightCostId?: number | null;
  customsCodeId?: number | null;
  sampleCharge: number;
  mouldCharge: number;
  surcharge: number;
  supplierContactId?: number | null;
  mpq?: number | null;
  sampleLeadTime?: number | null;
  bulkLeadTime?: number | null;
  shipmentLeadTime?: number | null;
  developmentCost?: number | null;
  developmentRefundQuantity?: number | null;
  developmentRefundTimeMonths?: number | null;
}

export interface UpdateInquirySentRequest {
  sent: string | null;
}

export interface UpdateInquiryInternalOwnerRequest {
  internalOwnerId: number | null;
}

export interface UpdateInquirySupplierContactRequest {
  supplierContactId: number | null;
}

export interface AutoUpdateInquiryTierPrice {
  price?: number | null;
}
export const EMPTY_AUTO_UPDATE_INQUIRY_TIER_PRICE: AutoUpdateInquiryTierPrice =
  { price: 0 };
export interface AutoUpdateInquiryTierPriceAdditionalData {
  inquiryId: number;
}

export interface TierPriceUpdate {
  id: number;
  quantity: number;
  price: number | null;
}

export interface AcceptInquiryTierPricesRequest {
  tierPriceIds: number[];
}

export interface RejectInquiryTierPricesRequest {
  tierPriceIds: number[];
}

export interface ReviewInquiryTierPricesRequest {
  tierPriceIds: number[];
}

export interface CreateInquiryTierPriceVersionsRequest {
  tierPriceIds: number[];
}

export interface InquiryHistories {
  id: number;
  histories: EntityHistory<InquiryHistoryEvent>[];
}

export const inquiryApi = {
  getInquiry: (id: number) =>
    httpClient.get<SupplierInquiry>(`/inquiries/${id}`),
  updateInquiry: (id: number, data: UpdateInquiryRequest) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}`, { data }),
  archiveInquiry: (id: number) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/archive`),
  restoreInquiry: (id: number) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/restore`),
  copyInquiry: (id: number) =>
    httpClient.post<SupplierInquiry>(`/inquiries/${id}/copy`),
  accept: (id: number) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/ready`),
  unaccept: (id: number) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/unready`),
  acceptInquiry: (id: number) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/accept`),
  rejectInquiry: (id: number) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/reject`),
  getByVariant: (filters: InquiryByVariantFilters) =>
    httpClient.post<InquiryPageData>(`/inquiries/by-variant`, {
      data: filters,
    }),
  getCountsByVariant: (filters: InquiryCountFilters) =>
    httpClient.post<InquiryCounts>(`/inquiries/by-variant/counts`, {
      data: filters,
    }),
  createInquiry: (compositionGroupPurchaseInfoId: number) =>
    httpClient.post<SupplierInquiry>(`/inquiries`, {
      data: { compositionGroupPurchaseInfoId },
    }),
  addTierPrice: (inquiryId: number, quantity: number, price: number | null) =>
    httpClient.post<TierPrice>(`/inquiries/${inquiryId}/tier-prices`, {
      data: { quantity, price },
    }),
  updateTierPrice: (inquiryId: number, tierId: number, price: number | null) =>
    httpClient.put<TierPrice>(`/inquiries/${inquiryId}/tier-prices/${tierId}`, {
      data: { price },
    }),
  autoUpdateTierPrice: (
    request: AutoUpdateRequestWithData<
      AutoUpdateInquiryTierPrice,
      AutoUpdateInquiryTierPriceAdditionalData
    >,
    tierId: number,
    inquiryId: number,
  ) =>
    httpClient.post<AutoUpdateResponse<TierPrice>>(
      `/inquiries/${inquiryId}/tier-prices/${tierId}/auto`,
      { data: request },
    ),
  deleteTierPrice: (inquiryId: number, tierId: number) =>
    httpClient.delete<void>(`/inquiries/${inquiryId}/tier-prices/${tierId}`),
  getHistory: (inquiryId: number) =>
    httpClient.get<InquiryHistories>(`/inquiries/${inquiryId}/history`),
  batchUpdateTierPrices: (id: number, updates: TierPriceUpdate[]) =>
    httpClient.put<TierPrice[]>(`/inquiries/${id}/tier-prices/batch`, {
      data: updates,
    }),
  setMainTierPrice: (inquiryId: number, tierId: number) =>
    httpClient.put<TierPrice>(
      `/inquiries/${inquiryId}/tier-prices/${tierId}/main`,
    ),
  setMainInquiry: (id: number) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/set-main`),
  acceptTierPrices: (data: AcceptInquiryTierPricesRequest) =>
    httpClient.put<void>(`/inquiries/tier-prices/accept`, { data }),
  rejectTierPrices: (data: RejectInquiryTierPricesRequest) =>
    httpClient.put<void>(`/inquiries/tier-prices/reject`, { data }),
  reviewTierPrices: (data: ReviewInquiryTierPricesRequest) =>
    httpClient.put<void>(`/inquiries/tier-prices/review`, { data }),
  createTierPriceVersion: (tierPriceId: number) =>
    httpClient.post<TierPrice>(
      `/inquiries/tier-prices/${tierPriceId}/version`,
      {
        data: {},
      },
    ),
  createTierPriceVersions: (data: CreateInquiryTierPriceVersionsRequest) =>
    httpClient.put<TierPrice[]>(`/inquiries/tier-prices/version`, { data }),
  getTierPriceHistory: (tierPriceId: number) =>
    httpClient.get<TierPrice[]>(
      `/inquiries/tier-prices/${tierPriceId}/history`,
    ),
  sendInquiry: (id: number) =>
    httpClient.post<SupplierInquiry>(`/inquiries/${id}/send`),
  updateSent: (id: number, data: UpdateInquirySentRequest) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/sent`, { data }),
  updateInternalOwner: (id: number, data: UpdateInquiryInternalOwnerRequest) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/internal-owner`, {
      data,
    }),
  updateSupplierContact: (
    id: number,
    data: UpdateInquirySupplierContactRequest,
  ) =>
    httpClient.put<SupplierInquiry>(`/inquiries/${id}/supplier-contact`, {
      data,
    }),
};
